// The `dev:device` script bakes a build timestamp into the native WebView's
// server URL (see apps/s-process/mobile/scripts/dev-device.ts) so the dev-only
// build badge can report when THIS on-device build was installed — not when the
// dev server it talks to started. That distinction matters because dev:device
// rebuilds and reinstalls the native shell but points it at the already-running
// dev server, which never restarts between device rebuilds, so the badge's
// server-start-based time would sit frozen across builds.
//
// The value is epoch-ms in a `?sProcessBuildAt=` query param. We read it once at
// bootstrap, keep it in sessionStorage so it survives an in-app reload (e.g. a
// vite:preloadError recovery reload navigates away from the stamped URL), and
// strip it from the URL so it doesn't linger in the address bar or copied links.
const BUILD_STAMP_PARAM = "sProcessBuildAt";
const BUILD_STAMP_STORAGE_KEY = "sProcess.devBuildStamp";

function parseStamp(raw: string | null): number | null {
  if (!raw) {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function readStoredStamp(): number | null {
  try {
    return parseStamp(window.sessionStorage.getItem(BUILD_STAMP_STORAGE_KEY));
  } catch {
    return null;
  }
}

function captureFromUrl(): number | null {
  try {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(BUILD_STAMP_PARAM)) {
      return null;
    }

    const stamp = parseStamp(url.searchParams.get(BUILD_STAMP_PARAM));

    url.searchParams.delete(BUILD_STAMP_PARAM);
    window.history.replaceState(window.history.state, "", url);

    if (stamp != null) {
      try {
        window.sessionStorage.setItem(BUILD_STAMP_STORAGE_KEY, String(stamp));
      } catch {
        // Storage blocked (private mode / lockdown) — the in-memory value below
        // still covers this page load; only reloads lose the stamp.
      }
    }

    return stamp;
  } catch {
    return null;
  }
}

// Prefer a freshly-baked URL param (a new dev:device install) over the stored
// value (a reload within the same WebView session). Read once at module load,
// before the router can rewrite the URL.
const devBuildStamp: number | null = captureFromUrl() ?? readStoredStamp();

/**
 * Epoch-ms timestamp of when the current on-device dev build was installed via
 * `bun run dev:device`, or null everywhere else (desktop dev, deployed builds).
 */
export function getDevBuildStamp(): number | null {
  return devBuildStamp;
}
